// src/features/tournament/components/TournamentList.tsx

import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Loader2,
  Plus,
  RefreshCw,
  Trophy,
  Users,
} from 'lucide-react';

import {
  getTournamentParticipants,
  getTournaments,
} from '../services/tournament.service';

import type {
  TournamentListItem,
  TournamentParticipantsData,
} from '../types/tournament.types';

import {
  formatMonthKey,
  formatSeed,
  formatTournamentDate,
  parseMonthKey,
} from '../utils/tournament.utils';

import TournamentParticipantList
  from './TournamentParticipantList';

import TournamentParticipantModal
  from './TournamentParticipantModal';


type TournamentListProps = {
  selectedMonth: string;

  onMonthChange: (
    month: string,
  ) => void;
};


const TournamentList:
  React.FC<TournamentListProps> = ({
    selectedMonth,
    onMonthChange,
  }) => {
    const [
      tournaments,
      setTournaments,
    ] =
      useState<
        TournamentListItem[]
      >([]);


    const [
      isLoading,
      setIsLoading,
    ] =
      useState(false);


    const [
      error,
      setError,
    ] =
      useState('');


    const [
      expandedTournamentId,
      setExpandedTournamentId,
    ] =
      useState<number | null>(
        null,
      );


    const [
      participants,
      setParticipants,
    ] =
      useState<
        Record<
          number,
          TournamentParticipantsData
        >
      >({});


    const [
      participantLoadingId,
      setParticipantLoadingId,
    ] =
      useState<number | null>(
        null,
      );


    const [
      participantModalTournament,
      setParticipantModalTournament,
    ] =
      useState<
        TournamentListItem | null
      >(
        null,
      );


    const loadTournaments =
      useCallback(
        async (): Promise<void> => {
          try {
            setIsLoading(
              true,
            );

            setError(
              '',
            );


            const data =
              await getTournaments(
                selectedMonth,
              );


            setTournaments(
              data,
            );


            setExpandedTournamentId(
              null,
            );


            setParticipants(
              {},
            );

          } catch (
            error
          ) {
            console.error(
              '대회 목록 조회 실패:',
              error,
            );


            setTournaments(
              [],
            );


            setError(
              '대회 목록을 불러오지 못했습니다.',
            );

          } finally {
            setIsLoading(
              false,
            );
          }
        },
        [
          selectedMonth,
        ],
      );


    const loadParticipants =
      useCallback(
        async (
          tournamentId:
            number,
        ): Promise<void> => {
          try {
            setParticipantLoadingId(
              tournamentId,
            );


            const data =
              await getTournamentParticipants(
                tournamentId,
              );


            setParticipants(
              (
                previous,
              ) => ({
                ...previous,

                [tournamentId]:
                  data,
              }),
            );


            setTournaments(
              (
                previous,
              ) =>
                previous.map(
                  (
                    tournament,
                  ) =>
                    tournament.tournamentId ===
                    tournamentId
                      ? {
                          ...tournament,

                          participantCount:
                            data.entryCount,
                        }
                      : tournament,
                ),
            );

          } catch (
            error
          ) {
            console.error(
              '대회 참가자 조회 실패:',
              error,
            );


            window.alert(
              '참가자 목록을 불러오지 못했습니다.',
            );

          } finally {
            setParticipantLoadingId(
              null,
            );
          }
        },
        [],
      );


    const handleToggle =
      async (
        tournamentId:
          number,
      ): Promise<void> => {
        if (
          expandedTournamentId ===
          tournamentId
        ) {
          setExpandedTournamentId(
            null,
          );

          return;
        }


        setExpandedTournamentId(
          tournamentId,
        );


        if (
          !participants[
            tournamentId
          ]
        ) {
          await loadParticipants(
            tournamentId,
          );
        }
      };


    const handlePreviousMonth =
      (): void => {
        const date =
          parseMonthKey(
            selectedMonth,
          );


        date.setMonth(
          date.getMonth() -
            1,
        );


        onMonthChange(
          formatMonthKey(
            date,
          ),
        );
      };


    const handleNextMonth =
      (): void => {
        const date =
          parseMonthKey(
            selectedMonth,
          );


        date.setMonth(
          date.getMonth() +
            1,
        );


        onMonthChange(
          formatMonthKey(
            date,
          ),
        );
      };


    const handleCurrentMonth =
      (): void => {
        onMonthChange(
          formatMonthKey(
            new Date(),
          ),
        );
      };


    useEffect(
      () => {
        void loadTournaments();
      },
      [
        loadTournaments,
      ],
    );


    return (
      <>

        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">

          <div className="mb-5 space-y-4">

            <div className="flex items-start justify-between gap-4">

              <div>

                <h2 className="text-xl font-bold text-gray-900">
                  대회 목록
                </h2>


                <p className="mt-1 text-sm text-gray-500">
                  대회를 펼치면 참가 엔트리를 확인하고 관리할 수 있습니다.
                </p>

              </div>


              <button
                type="button"
                onClick={() =>
                  void loadTournaments()
                }
                disabled={
                  isLoading
                }
                className="flex h-10 shrink-0 items-center gap-2 rounded-xl border border-gray-300 px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >

                <RefreshCw
                  size={16}
                  className={
                    isLoading
                      ? 'animate-spin'
                      : ''
                  }
                />


                <span className="hidden sm:inline">
                  새로고침
                </span>

              </button>

            </div>


            <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">

              <div className="flex items-center justify-center gap-2">

                <button
                  type="button"
                  onClick={
                    handlePreviousMonth
                  }
                  disabled={
                    isLoading
                  }
                  className="grid h-10 w-10 place-items-center rounded-xl border border-gray-300 bg-white text-gray-600"
                >
                  <ChevronLeft
                    size={20}
                  />
                </button>


                <input
                  type="month"
                  value={
                    selectedMonth
                  }
                  onChange={(
                    event,
                  ) =>
                    onMonthChange(
                      event.target.value,
                    )
                  }
                  disabled={
                    isLoading
                  }
                  className="h-10 min-w-[170px] rounded-xl border border-gray-300 bg-white px-3 text-center text-sm font-bold"
                />


                <button
                  type="button"
                  onClick={
                    handleNextMonth
                  }
                  disabled={
                    isLoading
                  }
                  className="grid h-10 w-10 place-items-center rounded-xl border border-gray-300 bg-white text-gray-600"
                >
                  <ChevronRight
                    size={20}
                  />
                </button>

              </div>


              <div className="flex items-center justify-between gap-3">

                <span className="text-sm font-semibold text-gray-700">
                  {selectedMonth.replace(
                    '-',
                    '년 ',
                  )}
                  월
                </span>


                <button
                  type="button"
                  onClick={
                    handleCurrentMonth
                  }
                  disabled={
                    isLoading
                  }
                  className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-600"
                >
                  이번 달
                </button>

              </div>

            </div>

          </div>


          {isLoading ? (

            <div className="grid min-h-[360px] place-items-center">

              <Loader2
                size={30}
                className="animate-spin text-main"
              />

            </div>

          ) : error ? (

            <div className="grid min-h-[360px] place-items-center text-center">

              <div>

                <p className="font-semibold text-red-600">
                  {error}
                </p>


                <button
                  type="button"
                  onClick={() =>
                    void loadTournaments()
                  }
                  className="mt-4 rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
                >
                  다시 시도
                </button>

              </div>

            </div>

          ) : tournaments.length ===
            0 ? (

            <div className="grid min-h-[360px] place-items-center text-center text-gray-500">

              <div>

                <Trophy
                  size={34}
                  className="mx-auto text-gray-300"
                />


                <p className="mt-3 text-sm">
                  등록된 대회가 없습니다.
                </p>

              </div>

            </div>

          ) : (

            <div className="space-y-3">

              {tournaments.map(
                (
                  tournament,
                ) => {
                  const expanded =
                    expandedTournamentId ===
                    tournament.tournamentId;


                  const participantData =
                    participants[
                      tournament.tournamentId
                    ];


                  return (
                    <article
                      key={
                        tournament.tournamentId
                      }
                      className="overflow-hidden rounded-2xl border border-gray-200"
                    >

                      <button
                        type="button"
                        onClick={() =>
                          void handleToggle(
                            tournament.tournamentId,
                          )
                        }
                        className="flex w-full items-center gap-4 bg-white p-4 text-left transition hover:bg-gray-50"
                      >

                        <div className="h-20 w-16 shrink-0 overflow-hidden rounded-xl bg-gray-100">

                          <img
                            src={
                              tournament.tournamentPoster
                            }
                            alt={
                              tournament.tournamentTitle
                            }
                            className="h-full w-full object-cover"
                          />

                        </div>


                        <div className="min-w-0 flex-1">

                          <h3 className="truncate font-bold">
                            {
                              tournament.tournamentTitle
                            }
                          </h3>


                          <p className="mt-1 truncate text-sm text-gray-500">
                            {formatTournamentDate(
                              tournament.tournamentTime,
                            )}
                          </p>


                          <p className="mt-1 text-xs font-semibold text-gray-600">
                            참가비{' '}
                            {formatSeed(
                              tournament.tournamentPrice,
                            )}
                          </p>

                        </div>


                        <div className="shrink-0 text-right">

                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700">

                            <Users
                              size={14}
                            />

                            {
                              tournament.participantCount
                            }
                            /
                            {
                              tournament.tournamentEntry
                            }

                          </span>


                          <div className="mt-2 flex justify-end text-gray-400">

                            {expanded ? (
                              <ChevronUp
                                size={20}
                              />
                            ) : (
                              <ChevronDown
                                size={20}
                              />
                            )}

                          </div>

                        </div>

                      </button>


                      {expanded && (

                        <div className="border-t border-gray-200 bg-gray-50 p-3 sm:p-4">

                          <div className="mb-3 flex items-center justify-between gap-3">

                            <div>

                              <strong className="text-sm">
                                참가 엔트리
                              </strong>

                              <p className="mt-0.5 text-xs text-gray-500">
                                시스템 관리자가 회원을 직접 추가할 수 있습니다.
                              </p>

                            </div>


                            <button
                              type="button"
                              onClick={() =>
                                setParticipantModalTournament(
                                  tournament,
                                )
                              }
                              className="flex h-9 items-center gap-1.5 rounded-lg bg-main px-3 text-xs font-bold text-white"
                            >

                              <Plus
                                size={15}
                              />

                              참가자 추가

                            </button>

                          </div>


                          {participantLoadingId ===
                          tournament.tournamentId ? (

                            <div className="flex min-h-28 items-center justify-center">

                              <Loader2
                                size={24}
                                className="animate-spin text-main"
                              />

                            </div>

                          ) : participantData ? (

                            <TournamentParticipantList
                              tournament={
                                tournament
                              }
                              data={
                                participantData
                              }
                              onReload={() =>
                                loadParticipants(
                                  tournament.tournamentId,
                                )
                              }
                            />

                          ) : null}

                        </div>

                      )}

                    </article>
                  );
                },
              )}

            </div>

          )}

        </section>


        {participantModalTournament && (

          <TournamentParticipantModal
            tournament={
              participantModalTournament
            }
            onClose={() =>
              setParticipantModalTournament(
                null,
              )
            }
            onAdded={() =>
              loadParticipants(
                participantModalTournament
                  .tournamentId,
              )
            }
          />

        )}

      </>
    );
  };


export default TournamentList;